// Cambios clave: uso de queryParams, un solo useEffect, y funciones actualizadoras
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Fragment,
} from "react";
import Swal from "sweetalert2";
import {
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
  LinearProgress,
  Fade,
  Box,
  Collapse,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ReactPaginate from "react-paginate";
import Table from "../../Components/Common/Table/Table";
import {
  getInfoMessageNoResultsSearch,
  CommonSubHeader,
} from "../../Components/helpers/Helpers";
import TableActions from "../../Components/Common/Table/TableActions";
import {
  Groups3,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import api from "../../api/axios";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { ChevronLeft, ChevronRight } from "lucide-react";

const agreementOptions = [
  { label: "Seleccionar", value: null },
  { label: "NO", value: 0 },
  { label: "Acu. Directo", value: 1 },
];

function Associations() {
  const [data, setData] = useState({ associations: [] });
  const [changeDone, setChangeDone] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    setQueryParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        pagePressed: 1,
      },
    }));
  };

  const [dataTable, setDataTable] = useState({
    totalPagesNumber: 0,
    pageCount: 0,
  });
  const [flag, setFlag] = useState(false);
  const [options, setOptions] = useState({
    associations: [],
    provinces: [],
    groups: [],
    companies: [],
  });
  const [loading, setLoading] = useState({
    associations: false,
    provinces: false,
    groups: false,
    companies: false,
  });
  const [query, setQuery] = useState({ phone: "", email: "" });

  //   Un solo estado unificado para filtros y paginación
  const [queryParams, setQueryParams] = useState({
    filters: {
      associationId: 0,
      groupId: 0,
      companyId: 0,
      provinceFilter: 0,
      phoneFilter: "",
      emailFilter: "",
      agreementFilter: null,
    },
    pagination: {
      objPerPage: 10,
      pagePressed: 1,
    },
  });

  const getDataAssociations = async () => {
    setFlag(true);

    if (queryParams?.pagination.objPerPage >= 500) {
      Swal.fire({
        icon: "warning",
        title: "Carga de registros extensa",
        text: "Ha seleccionado un volumen elevado de registros. El proceso de carga puede tardar algunos segundos, le agradecemos su paciencia mientras se completan los archivos.",
        confirmButtonText: "Aceptar",
      });
    }

    const params = {
      page: queryParams?.pagination.pagePressed,
      objPerPage: queryParams?.pagination.objPerPage,
      company_id: queryParams?.filters.companyId,
      asoc_id: queryParams?.filters.associationId,
      asoc_grupo: queryParams?.filters.groupId,
      province_name: queryParams?.filters.provinceFilter,
      phone: queryParams?.filters.phoneFilter,
      email: queryParams?.filters.emailFilter,
      asoc_acuerdo: queryParams?.filters.agreementFilter,
      sortColumn,
      sortDirection,
    };

    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/associations`,
        params,
      );

      if (response?.data?.associations) {
        const { associations, groupsList, provincesList, companyList } =
          response.data;
        setData({ associations: associations.data });
        setDataTable({
          totalPagesNumber: associations.total,
          pageCount: associations.last_page,
        });
        setOptions((prevOptions) => ({
          ...prevOptions,
          associations: [
            { asoc_name: "Seleccionar", id: 0 },
            ...response.data.associationsList,
          ],
          groups: [{ grupo: "Seleccionar", id: 0 }, ...groupsList],
          provinces: [
            { provincia: "Seleccionar", id_provincia: 0 },
            ...provincesList,
          ],
          companies: [{ company_name: "Seleccionar", id: 0 }, ...companyList],
        }));

        if (associations.total === 0) {
          getInfoMessageNoResultsSearch(
            "¡No se encontraron resultados!",
            "Por favor cambia los filtros o realiza una nueva búsqueda",
            "error",
            true,
            "Continuar",
            false,
          );
        } else {
          Swal.close();
        }
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las asociaciones",
        confirmButtonColor: "#1ab394",
      });
    } finally {
      setFlag(false);
      setLoading((prev) => ({ ...prev, companies: false }));
    }
  };

  useEffect(() => {
    getDataAssociations();
  }, [JSON.stringify(queryParams), changeDone, sortColumn, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  //   Actualización con debounce
  const updateFilter = useCallback((field, value) => {
    setQueryParams((prev) => ({
      ...prev,
      filters: { ...prev.filters, [field]: value },
    }));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => updateFilter("phoneFilter", query.phone),
      1500,
    );
    return () => clearTimeout(timeoutId);
  }, [query.phone, updateFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => updateFilter("emailFilter", query.email),
      1500,
    );
    return () => clearTimeout(timeoutId);
  }, [query.email, updateFilter]);

  const handlePageClick = (event) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pagePressed: event.selected + 1 },
    }));
  };

  const handleAutocompleteOpen = useCallback((field) => {
    setLoading((prev) => ({ ...prev, [field]: true }));
  }, []);

  const renderCellWithDefault = (value, defaultValue = "Sin asignar") => (
    <div className="spanClassForTables centerContent">
      {value || defaultValue}
    </div>
  );

  const renderAgreement = (value) => (
    <div className="spanClassForTables centerContent">
      {value === 1 ? "Acu. Directo" : "NO"}
    </div>
  );

  const columns = useMemo(
    () => [
      {
        Header: `LISTA DE ASOCIACIONES`,
        columns: [
          {
            Header: "Asociación",
            sortField: "asoc_name",
            accessor: "asoc_name",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Grupo",
            sortField: "asoc_grupo",
            accessor: "group_relations.grupo",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Acuerdo",
            sortField: "asoc_acuerdo",
            accessor: "asoc_acuerdo",
            Cell: ({ value }) => renderAgreement(value),
          },
          {
            Header: "Provincia",
            sortField: "asoc_province",
            accessor: "asoc_province",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Teléfono",
            sortField: "asoc_phone",
            accessor: "asoc_phone",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Email",
            sortField: "asoc_email",
            accessor: "asoc_email",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Acciones",
            accessor: "id",
            Cell: ({ value }) => (
              <TableActions
                rute={"asociaciones"}
                value={value}
                showEdit
                showView
                showDelete
                module={"association"}
                apiRute={"associations"}
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            ),
          },
        ],
      },
    ],
    [],
  );

  const filterFields = [
    {
      type: "autocomplete",
      label: "Asociación",
      stateKey: "associationId",
      optionKey: "associations",
      optionLabel: "asoc_name",
    },
    {
      type: "autocomplete",
      label: "Grupo",
      stateKey: "groupId",
      optionKey: "groups",
      optionLabel: "grupo",
    },
    {
      type: "autocomplete",
      label: "Empresa",
      stateKey: "companyId",
      optionKey: "companies",
      optionLabel: "company_name",
    },
    {
      type: "autocomplete",
      label: "Provincia",
      stateKey: "provinceFilter",
      optionKey: "provinces",
      optionLabel: "provincia",
    },
    {
      type: "select",
      label: "Acuerdos",
      stateKey: "agreementFilter",
      options: agreementOptions,
    },
    {
      type: "textField",
      label: "Teléfono",
      stateKey: "phone",
    },
    {
      type: "textField",
      label: "Email",
      stateKey: "email",
    },
  ];

  useEffect(() => {
    if (
      dataTable.pageCount > 0 &&
      queryParams?.pagination.pagePressed > dataTable.pageCount
    ) {
      setQueryParams((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, pagePressed: 1 },
      }));
    }
  }, [dataTable.pageCount, queryParams?.pagination.pagePressed]);

  const content = (
    <div>
      <Box
        sx={{
          position: "relative",
          background: "white",
          marginBottom: "33px",
          mx: "10px",
          borderRadius: "10px",
        }}
      >
        <Collapse in={showFilters} timeout={400}>
          <Grid
            container
            className="filtersContainer"
            sx={{
              boxShadow: `0 1px 2px rgba(0,0,0,0.05),0 -8px 38px rgba(0,0,0,0.08)`,
            }}
          >
            {filterFields?.map((field, index) => (
              <Grid key={index} item xs={4} md={2} sx={{ padding: "5px" }}>
                {field.type === "autocomplete" ? (
                  <Autocomplete
                    size="small"
                    value={
                      options[field.optionKey].find((opt) => {
                        const val = queryParams?.filters[field.stateKey];
                        if (field.stateKey === "provinceFilter")
                          return opt.provincia === val;
                        return opt.id === val;
                      }) || null
                    }
                    onChange={(event, value) =>
                      setQueryParams((prev) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          [field.stateKey]: value
                            ? value.provincia
                              ? value.provincia
                              : value.id
                            : 0,
                        },
                      }))
                    }
                    open={loading[field.optionKey]}
                    onOpen={() => handleAutocompleteOpen(field.optionKey)}
                    onClose={() =>
                      setLoading((prev) => ({
                        ...prev,
                        [field.optionKey]: false,
                      }))
                    }
                    getOptionLabel={(option) =>
                      option[field.optionLabel] || "Seleccionar"
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id ||
                      option.provincia === value.provincia
                    }
                    options={options[field.optionKey]}
                    loading={loading[field.optionKey]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        autoComplete="new-password"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <Fragment>
                              {loading[field.optionKey] && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                ) : field.type === "select" ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      value={queryParams?.filters[field.stateKey] ?? ""}
                      label={field.label}
                      onChange={(e) =>
                        setQueryParams((prev) => ({
                          ...prev,
                          filters: {
                            ...prev.filters,
                            [field.stateKey]: e.target.value,
                          },
                        }))
                      }
                    >
                      {field.options?.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label={field.label}
                    autoComplete="new-password"
                    value={query[field.stateKey] || ""}
                    onChange={(e) =>
                      setQuery((prev) => ({
                        ...prev,
                        [field.stateKey]: e.target.value,
                      }))
                    }
                  />
                )}
              </Grid>
            ))}
            {/* Selector de Número de Registros */}
            <Grid item xs={4} md={2} sx={{ padding: "5px" }}>
              <FormControl fullWidth size="small">
                <InputLabel>Nº Registros</InputLabel>
                <Select
                  value={queryParams?.pagination.objPerPage || 10}
                  label="Nº Registros"
                  onChange={(e) => {
                    setQueryParams((prev) => ({
                      ...prev,
                      pagination: {
                        ...prev.pagination,
                        objPerPage: Number(e.target.value),
                        pagePressed: 1,
                      },
                    }));
                  }}
                >
                  {[10, 20, 30, 100, 200, 500, 1000, 2000]?.map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>
        {/* BOTÓN FLOTANTE */}
        <Box
          sx={{
            position: "absolute",
            bottom: showFilters ? "-26px" : "-16px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            endIcon={showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            sx={{
              minWidth: "220px",
              height: "25px",
              borderRadius: "0px 0px 10px 10px",
              boxShadow: 3,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
          </Button>
        </Box>
      </Box>

      {flag && <LoadingPage isLoading={flag} heightProp={"80vh"} />}

      {!flag && (
        <Fade in={!flag} timeout={800}>
          <div>
            <Table
              columns={columns}
              data={data.associations}
              view="asociacion"
              viewTitle="asociaciones"
              totalPagesNumber={dataTable.totalPagesNumber}
              tableWidth="unset"
              minTableWidth="80%"
              icon={Groups3}
              viewPermission={"association"}
              apiRoute={"associations"}
              // pagination
              onPageChange={handlePageClick}
              pageCount={dataTable.pageCount}
              forcePage={queryParams?.pagination.pagePressed - 1} // Esto sincroniza el botón activo
              objPerPage={queryParams?.pagination.objPerPage}
              // SORT
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              setChangeDone={setChangeDone}
              changeDone={changeDone}
            />
          </div>
        </Fade>
      )}
    </div>
  );

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Configuración" subTitle="Asociaciones" />

      <div className="mainContainer">
        <div className="subMainContainer">{content}</div>
      </div>
    </div>
  );
}

export default Associations;
